import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./_utils";

const progressFillVariants = cva("h-full rounded-full transition-all", {
  variants: {
    variant: {
      default: "bg-accent",
      success: "bg-success",
      warning: "bg-warning",
      error: "bg-error",
    },
  },
  defaultVariants: { variant: "default" },
});

type ProgressBarProps = Omit<React.HTMLAttributes<HTMLDivElement>, "children"> &
  VariantProps<typeof progressFillVariants> & {
    value: number;
  };

/**
 * Horizontal determinate progress bar. `value` is clamped to 0–100.
 *
 * @example
 * <ProgressBar value={40} />
 * <ProgressBar value={80} variant="success" />
 * <ProgressBar value={30} variant="warning" />
 * <ProgressBar value={90} variant="error" />
 */
export function ProgressBar({
  className,
  variant,
  value,
  ...props
}: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn(
        "h-1.5 bg-[var(--print-bg)] rounded-full overflow-hidden",
        className
      )}
      {...props}
    >
      <div
        className={cn(progressFillVariants({ variant }))}
        style={{ width: `${clamped}%` }}
        data-testid="progress-fill"
      />
    </div>
  );
}
